import React, { useEffect, useState } from "react";
import { Button } from "../../design/button";
import {
  Dialog,
  DialogActions,
  DialogDescription,
  DialogBody,
  DialogTitle,
} from "../../design/dialog";
import { Input } from "../../design/input";
import { ErrorMessage, Field, Fieldset, FieldGroup, Label } from "../../design/fieldset";
import { Select } from "../../design/select";
import { Text } from "../../design/text";
import { useVisit } from "../../contexts/VisitContext";
import { useSupervisor } from "../../contexts/SupervisorContext";
import { useCase } from "../../contexts/CaseContext";
import { useLocation } from "../../contexts/LocationContext";
import { Timestamp } from "firebase/firestore";
import { Textarea } from "../../design/textarea";
import { Switch, SwitchField } from "../../design/switch";
import { Visit } from "@visitproof/types";
import { CaseService } from "../../services/CaseService";
import moment from "moment";
import { toLocalDateString, toLocalTimeString } from "../../utils/timeString";
import {
  CheckCircleIcon,
  FolderIcon,
  CalendarDaysIcon,
  MapPinIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

interface Props {
  title: string;
  visit: Partial<Visit>;
  onClose: () => void;
  onVisitCreated?: (visitId: string) => void;
}

interface Errors {
  startDate?: string;
  endDate?: string;
  caseId?: string;
  supervisorId?: string;
  general?: string;
}

type Step = 1 | 2 | 3 | 4;

// Step indicator component
const StepIndicator: React.FC<{
  currentStep: Step;
  totalSteps: number;
  stepLabels: string[];
}> = ({ currentStep, totalSteps, stepLabels }) => (
  <div className="mb-6">
    <div className="flex items-center justify-between">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                step < currentStep
                  ? "bg-green-500 text-white"
                  : step === currentStep
                    ? "bg-blue-500 text-white"
                    : "bg-zinc-200 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400"
              }`}
            >
              {step < currentStep ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                step
              )}
            </div>
            <span
              className={`mt-1 text-xs ${
                step === currentStep
                  ? "font-medium text-zinc-900 dark:text-white"
                  : "text-zinc-500 dark:text-zinc-400"
              }`}
            >
              {stepLabels[step - 1]}
            </span>
          </div>
          {step < totalSteps && (
            <div
              className={`mx-2 h-0.5 flex-1 ${
                step < currentStep
                  ? "bg-green-500"
                  : "bg-zinc-200 dark:bg-zinc-700"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// Section header component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  description?: string;
}> = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
      <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
    </div>
    <div>
      <Text className="font-semibold text-zinc-900 dark:text-zinc-100">
        {title}
      </Text>
      {description && (
        <Text className="text-sm text-zinc-500 dark:text-zinc-400">
          {description}
        </Text>
      )}
    </div>
  </div>
);

const CreateVisitDialog: React.FC<Props> = ({
  title,
  visit,
  onClose,
  onVisitCreated,
}) => {
  const { cases } = useCase();
  const { locations } = useLocation();
  const { supervisors } = useSupervisor();
  const { createVisit } = useVisit();

  const [step, setStep] = useState<Step>(1);

  // Convert startDate and endDate to local time before setting state
  const [startDate, setStartDate] = useState<string>(
    toLocalDateString(visit.startDate?.toDate() ?? new Date()).slice(0, 10),
  );
  const [endDate, setEndDate] = useState<string>(
    toLocalDateString(visit.endDate?.toDate() ?? new Date()).slice(0, 10),
  );
  const [startTime, setStartTime] = useState<string>(
    toLocalTimeString(visit.startDate?.toDate() ?? new Date()).slice(11, 16),
  );
  const [endTime, setEndTime] = useState<string>(
    toLocalTimeString(visit.endDate?.toDate() ?? new Date()).slice(11, 16),
  );
  const [timeZoneId, setTimeZoneId] = useState(moment.tz.guess());
  const [locationId, setLocationId] = useState<string | null>(null);
  const [caseId, setCaseId] = useState<string | null>(visit.caseId ?? null);
  const [supervisorId, setSupervisorId] = useState<string | null>(
    visit.supervisorId ?? null,
  );
  const [notes, setNotes] = useState<string>("");
  const [markAsScheduled, setMarkAsScheduled] = useState(false);

  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Update local dates when props change
  useEffect(() => {
    setCaseId(visit.caseId ?? null);
    setLocationId(visit.locationId ?? null);
    setSupervisorId(visit.supervisorId ?? null);
    setNotes(visit.notes ?? "");
    setStartDate(
      toLocalDateString(visit.startDate?.toDate() ?? new Date()).slice(0, 10),
    );
    setEndDate(
      toLocalDateString(visit.endDate?.toDate() ?? new Date()).slice(0, 10),
    );
    setStartTime(
      toLocalTimeString(visit.startDate?.toDate() ?? new Date()).slice(11, 16),
    );
    setEndTime(
      toLocalTimeString(visit.endDate?.toDate() ?? new Date()).slice(11, 16),
    );
  }, [visit]);

  const validateStep = (currentStep: Step): boolean => {
    const newErrors: Errors = {};

    if (currentStep === 1) {
      if (!caseId) {
        newErrors.caseId = "Case is required.";
      }
    }

    if (currentStep === 2) {
      if (!startDate.trim()) {
        newErrors.startDate = "Start date is required.";
      }
      if (!endDate.trim()) {
        newErrors.endDate = "End date is required.";
      }
      const start = new Date(`${startDate}T${startTime}`);
      const end = new Date(`${endDate}T${endTime}`);
      if (start && end && new Date(start) > new Date(end)) {
        newErrors.startDate = "Start date must be before end date.";
      }
      if (end && start && new Date(end) < new Date(start)) {
        newErrors.endDate = "End date must be after start date.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, 4) as Step);
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1) as Step);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) {
      return;
    }
    setIsSaving(true);
    setErrors({});

    try {
      const caseItem = await CaseService.getCase(caseId!);

      let data = {
        status: markAsScheduled ? "scheduled" : "pending",
        caseId: caseId!,
        startDate: Timestamp.fromDate(new Date(`${startDate}T${startTime}`)),
        endDate: Timestamp.fromDate(new Date(`${endDate}T${endTime}`)),
        parents: caseItem?.parents,
        parentIds: caseItem?.parentIds,
        children: caseItem?.children,
        childIds: caseItem?.childIds,
        notes,
        timeZoneId,
      } as Visit;
      if (supervisorId) {
        data = {
          ...data,
          supervisorId: supervisorId!,
          supervisor: supervisors.find(
            (supervisor) => supervisor.id === supervisorId,
          )!,
        };
      }
      if (locationId) {
        data = {
          ...data,
          locationId: locationId!,
          location: locations.find((location) => location.id === locationId)!,
        };
      }

      const visitId = await createVisit(data);
      if (onVisitCreated) {
        onVisitCreated(visitId);
      }
      clearForm();
    } catch (e) {
      console.error("Error creating visit:", e);
      setErrors({
        general: e instanceof Error ? e.message : "Failed to create visit. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setStep(1);
    onClose();
  };

  const handleClose = () => {
    if (!isSaving) {
      clearForm();
    }
  };

  // Get selected case details for display
  const selectedCase = cases.find((c) => c.id === caseId);
  const selectedSupervisor = supervisors.find((s) => s.id === supervisorId);
  const selectedLocation = locations.find((l) => l.id === locationId);
  const filteredLocations = locations.filter((location) => location.caseId === caseId);

  const stepLabels = ["Case", "Date & Time", "Details", "Review"];

  return (
    <>
      <Dialog open onClose={handleClose} size="2xl">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {step === 1 && "Select the case for this visit."}
          {step === 2 && "Set the date, time, and timezone for this visit."}
          {step === 3 && "Assign a monitor and location for this visit."}
          {step === 4 && "Review the visit details and add any notes."}
        </DialogDescription>

        <DialogBody>
          <StepIndicator
            currentStep={step}
            totalSteps={4}
            stepLabels={stepLabels}
          />

          {errors.general && (
            <ErrorMessage className="mb-4">
              {errors.general}
            </ErrorMessage>
          )}

          {/* Step 1: Case Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <SectionHeader
                icon={FolderIcon}
                title="Select Case"
                description="Choose the case this visit is associated with"
              />

              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label htmlFor="case">
                      Case <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      id="case"
                      value={caseId || ""}
                      onChange={(e) => {
                        setCaseId(e.target.value);
                        // Reset location when case changes
                        setLocationId(null);
                        if (errors.caseId) {
                          setErrors((prev) => ({ ...prev, caseId: undefined }));
                        }
                      }}
                      disabled={isSaving}
                    >
                      <option value="">Select a case</option>
                      {cases
                        .filter((caseItem) => caseItem.status !== "closed")
                        .map((caseItem) => (
                          <option key={caseItem.id} value={caseItem.id}>
                            {`${caseItem.identifier ?? "Unnamed Case"} - (${Object.values(
                              caseItem.parents ?? {},
                            )
                              .map((p) => `${p.firstName} ${p.lastName}`)
                              .join(", ")})`}
                          </option>
                        ))}
                    </Select>
                    {errors.caseId && (
                      <ErrorMessage>{errors.caseId}</ErrorMessage>
                    )}
                    <Text className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                      Only open cases are shown. The visit will include parents and children from this case.
                    </Text>
                  </Field>
                </FieldGroup>
              </Fieldset>
            </div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <div className="space-y-6">
              <SectionHeader
                icon={CalendarDaysIcon}
                title="Date & Time"
                description="Set when this visit will take place"
              />

              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label htmlFor="startDate">
                      Start Date & Time <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Input
                        id="startDate"
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (errors.startDate) {
                            setErrors((prev) => ({ ...prev, startDate: undefined }));
                          }
                        }}
                        disabled={isSaving}
                      />
                      <Input
                        id="startTime"
                        type="time"
                        step="900"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        disabled={isSaving}
                      />
                    </div>
                    {errors.startDate && (
                      <ErrorMessage>{errors.startDate}</ErrorMessage>
                    )}
                  </Field>

                  <Field>
                    <Label htmlFor="endDate">
                      End Date & Time <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Input
                        id="endDate"
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          if (errors.endDate) {
                            setErrors((prev) => ({ ...prev, endDate: undefined }));
                          }
                        }}
                        disabled={isSaving}
                      />
                      <Input
                        id="endTime"
                        type="time"
                        step="900"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        disabled={isSaving}
                      />
                    </div>
                    {errors.endDate && (
                      <ErrorMessage>{errors.endDate}</ErrorMessage>
                    )}
                  </Field>

                  <Field>
                    <Label htmlFor="timeZone">Time Zone</Label>
                    <Select
                      id="timeZone"
                      value={timeZoneId}
                      onChange={(e) => setTimeZoneId(e.target.value)}
                      disabled={isSaving}
                    >
                      {moment.tz.names().map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </FieldGroup>
              </Fieldset>
            </div>
          )}

          {/* Step 3: Location & Monitor */}
          {step === 3 && (
            <div className="space-y-6">
              <SectionHeader
                icon={MapPinIcon}
                title="Location & Monitor"
                description="Assign a location and monitor for this visit"
              />

              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label htmlFor="supervisor">Monitor</Label>
                    <Select
                      id="supervisor"
                      value={supervisorId || ""}
                      onChange={(e) => setSupervisorId(e.target.value || null)}
                      disabled={isSaving}
                    >
                      <option value="">Select a monitor (optional)</option>
                      {supervisors
                        .filter((s) => s.status !== "inactive")
                        .map((supervisor) => (
                          <option key={supervisor.id} value={supervisor.id}>
                            {supervisor.firstName} {supervisor.lastName}
                          </option>
                        ))}
                    </Select>
                    <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                      The monitor will supervise this visit and can record events.
                    </Text>
                  </Field>

                  <Field>
                    <Label htmlFor="location">Location</Label>
                    <Select
                      id="location"
                      value={locationId || ""}
                      onChange={(e) => setLocationId(e.target.value || null)}
                      disabled={isSaving}
                    >
                      <option value="">Select a location (optional)</option>
                      {filteredLocations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </Select>
                    {filteredLocations.length === 0 && (
                      <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                        No locations configured for this case. You can add locations from the case details page.
                      </Text>
                    )}
                  </Field>
                </FieldGroup>
              </Fieldset>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-6">
              <SectionHeader
                icon={DocumentTextIcon}
                title="Review & Confirm"
                description="Review the visit details and add any notes"
              />

              {/* Visit Summary */}
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
                <h4 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-white">
                  Visit Summary
                </h4>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">Case</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {selectedCase?.identifier ?? "Unnamed Case"}
                      {selectedCase?.parents && Object.keys(selectedCase.parents).length > 0 && (
                        <span className="ml-2 text-zinc-500 dark:text-zinc-400">
                          ({Object.values(selectedCase.parents)
                            .map((p) => `${p.firstName} ${p.lastName}`)
                            .join(", ")})
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">Start</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {moment(`${startDate}T${startTime}`).format("MMMM D, YYYY")} at {moment(`${startDate}T${startTime}`).format("h:mm A")}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">End</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {moment(`${endDate}T${endTime}`).format("MMMM D, YYYY")} at {moment(`${endDate}T${endTime}`).format("h:mm A")}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">Time Zone</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {timeZoneId}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">Monitor</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {selectedSupervisor
                        ? `${selectedSupervisor.firstName} ${selectedSupervisor.lastName}`
                        : <span className="text-zinc-400 dark:text-zinc-500">Not assigned</span>}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">Location</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {selectedLocation
                        ? selectedLocation.name
                        : <span className="text-zinc-400 dark:text-zinc-500">Not specified</span>}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Notes */}
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      placeholder="Add any additional notes about this visit..."
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={isSaving}
                      rows={3}
                    />
                  </Field>

                  <SwitchField>
                    <Label>Mark as Scheduled</Label>
                    <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                      Enable this to set the visit status to "Scheduled" instead of "Pending"
                    </Text>
                    <Switch
                      checked={markAsScheduled}
                      onChange={setMarkAsScheduled}
                      disabled={isSaving}
                    />
                  </SwitchField>
                </FieldGroup>
              </Fieldset>
            </div>
          )}
        </DialogBody>

        <DialogActions>
          {step > 1 ? (
            <Button plain onClick={handleBack} disabled={isSaving}>
              Back
            </Button>
          ) : (
            <Button plain onClick={handleClose} disabled={isSaving}>
              Cancel
            </Button>
          )}
          {step < 4 ? (
            <Button onClick={handleNext} disabled={isSaving}>
              Continue
            </Button>
          ) : (
            <Button disabled={isSaving || !caseId} onClick={handleSubmit}>
              {isSaving ? "Creating..." : "Create Visit"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateVisitDialog;
